import React from "react";
import { renderToStaticMarkup } from "react-dom/server";

import { astToHtml, astToMarkdown } from "@/mk2/helpers/article_ast/filters";
import { getAttributesDict } from "@/mk2/helpers/article_ast/utils";

/**
 * AST node types the Froala richtext editor cannot edit inline. They are all
 * block-level wiki widgets. Instead of forcing the whole article into the markdown
 * editor, we render each of them as a non-editable "rawblock" card carrying its
 * original wiki markup, so the surrounding text stays richtext-editable and the widget
 * round-trips back untouched on save. Shared by the wiki and blog article editors.
 */
export const RICHTEXT_UNSUPPORTED_NODE_TYPES = [
    "reviews",
    "previews",
    "testing",
    "market",
    "strollers_chart",
    "iframe",
    "pricemania",
    "product_box",
    "pregnancy_newsletter",
    "pregnancy_calculator",
    "eshop",
    "prima",
];

/** Class marking a non-editable rawblock inside the Froala editor. */
export const RAWBLOCK_CLASS = "mk-rawblock";

const rawblockLabel = (type: string, title?: string): string => {
    // getAttributesDict keeps the surrounding quotes on the raw attribute value.
    const cleanTitle = title ? title.replace(/^["']|["']$/g, "") : "";
    return cleanTitle ? `${type} widget: "${cleanTitle}"` : `${type} widget`;
};

/**
 * True if a node must be kept out of the richtext editor and shown as a rawblock:
 * the block-level wiki widgets the editor cannot represent, plus cover pics (their
 * `cover` flag would be dropped by the plain <img> round-trip).
 */
const isRawblockNode = (node: any): boolean => {
    if (RICHTEXT_UNSUPPORTED_NODE_TYPES.includes(node.type)) {
        return true;
    }
    return node.type === "pic" && getAttributesDict(node)?.cover !== undefined;
};

interface Rawblock {
    sentinel: string;
    rawMarkdown: string;
    type: string;
    title?: string;
}

// Sentinel injected into the AST on load (swapped for a rawblock card in the HTML).
const astRawblockSentinel = (i: number) => `MKRAWBLOCKLOAD${i}X`;
// Sentinel injected into the HTML on save (swapped back for the markup after parsing).
const htmlRawblockSentinel = (i: number) => `MKRAWBLOCKSAVE${i}X`;

/**
 * Deep-clones the AST, replacing every rawblock node (at any depth) with a plain-text
 * sentinel leaf and recording its original wiki markup. The sentinels are later swapped
 * for non-editable cards in the rendered HTML.
 */
const replaceRawblocksWithSentinels = (node: any, additionalData: any, blocks: Rawblock[]) => {
    if (Array.isArray(node)) {
        return node.map((n) => replaceRawblocksWithSentinels(n, additionalData, blocks));
    }
    if (node && typeof node === "object") {
        if (isRawblockNode(node)) {
            const sentinel = astRawblockSentinel(blocks.length);
            blocks.push({
                sentinel,
                rawMarkdown: astToMarkdown(node, additionalData),
                type: node.type,
                title: getAttributesDict(node)?.title,
            });
            return { type: "plain_text", text: sentinel };
        }
        const clone: any = {};
        for (const key of Object.keys(node)) {
            clone[key] =
                key === "elements"
                    ? replaceRawblocksWithSentinels(node[key], additionalData, blocks)
                    : node[key];
        }
        return clone;
    }
    return node;
};

const renderRawblockHTML = (block: Rawblock): string =>
    renderToStaticMarkup(
        <div
            className={RAWBLOCK_CLASS}
            contentEditable={false}
            data-mk-raw={encodeURIComponent(block.rawMarkdown)}
            data-mk-raw-type={block.type}
        >
            {`⟨ ${rawblockLabel(block.type, block.title)} — editable in markdown mode ⟩`}
        </div>,
    );

const escapeForRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Renders an article AST into HTML for the Froala richtext editor. Block-level widgets
 * the editor cannot represent are rendered as non-editable rawblocks carrying their
 * original wiki markup (restored verbatim on save, see extractRawBlocks /
 * reinsertRawBlocks); every other node is rendered normally.
 */
export const astToHtmlWithRawBlocks = (ast: any, additionalData: any): string => {
    const blocks: Rawblock[] = [];
    const astWithSentinels = replaceRawblocksWithSentinels(ast, additionalData, blocks);
    let html = renderToStaticMarkup(
        <React.Fragment>{astToHtml(astWithSentinels, additionalData)}</React.Fragment>,
    );

    for (const block of blocks) {
        const blockHtml = renderRawblockHTML(block);
        // A standalone widget is rendered inside its own <p>; replace the whole paragraph
        // so the rawblock is a clean block. Fall back to a bare replace for inline positions.
        html = html
            .replace(
                new RegExp(`<p[^>]*>\\s*${escapeForRegExp(block.sentinel)}\\s*</p>`, "g"),
                blockHtml,
            )
            .split(block.sentinel)
            .join(blockHtml);
    }
    return html;
};

/** True if the Froala HTML contains any rawblock card. */
export const htmlHasRawBlocks = (html: string): boolean => html.includes(RAWBLOCK_CLASS);

/**
 * Replaces every rawblock in the Froala HTML with a plain-text sentinel and returns the
 * sentinel -> original-wiki-markup map. The sentinel survives the HTML -> AST -> markdown
 * parse as plain text; reinsertRawBlocks swaps the original markup back in afterwards, so
 * the widget is never mangled by the parser.
 */
export const extractRawBlocks = (
    html: string,
): { html: string; rawBlocks: { [sentinel: string]: string } } => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const blocks = doc.querySelectorAll(`.${RAWBLOCK_CLASS}`);
    const rawBlocks: { [sentinel: string]: string } = {};
    blocks.forEach((block, i) => {
        const sentinel = htmlRawblockSentinel(i);
        rawBlocks[sentinel] = decodeURIComponent(block.getAttribute("data-mk-raw") || "");
        const p = doc.createElement("p");
        p.textContent = sentinel;
        block.replaceWith(p);
    });
    return { html: doc.body.innerHTML, rawBlocks };
};

/** Swaps each sentinel back for its original wiki markup in the parsed markdown. */
export const reinsertRawBlocks = (
    markdown: string,
    rawBlocks: { [sentinel: string]: string },
): string => {
    let result = markdown;
    for (const [sentinel, rawMarkdown] of Object.entries(rawBlocks)) {
        result = result.split(sentinel).join(rawMarkdown);
    }
    return result;
};
